import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import QuestionnairePage from './components/QuestionnairePage';
import CompletionPage from './components/CompletionPage';
import RegisterPage from './components/RegisterPage';
import LoginPage from './components/LoginPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import UserProfilePage from './components/UserProfilePage';
import CreateCodePage from './components/CreateCodePage';
import VerificationErrorPage from './components/VerificationErrorPage';
import { questionnaireData } from './data/questionnaireData';
import apiClient from './config/axios';
import API_CONFIG from './config/api';

// Verification wrapper component
const VerificationWrapper = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [verifiedToken, setVerifiedToken] = useState(null);

  useEffect(() => {
    const verifyCode = async () => {
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      
      if (code) {
        setIsVerifying(true);
        try {
          const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.VERIFY_ONETIME, {
            token: code
          });
          
          if (response.status === 200) {
            // Verification successful, store the token and continue with normal flow
            setVerifiedToken(code);
            setIsVerifying(false);
          }
        } catch (error) {
          console.error('Verification error:', error);
          setVerificationError(true);
          setIsVerifying(false);
        }
      }
    };

    verifyCode();
  }, [location.search, navigate]);

  if (isVerifying) {
    return (
      <div className="questionnaire-page">
        <div className="page-header">
          <div className="page-info">
            <h2>Verifying Access</h2>
            <p className="page-description">
              Please wait while we verify your access code...
            </p>
          </div>
        </div>
        <div className="questions-container">
          <div className="question-block no-hover">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="success-icon">Verifying...</div>
              <p>Please wait...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationError) {
    return <VerificationErrorPage />;
  }

  // Clone children and pass the verified token
  return React.cloneElement(children, { verifiedToken });
};

// Header component that can access location
const AppHeader = ({ completedPages, isPageAccessible }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // Check if we're on an assessment page
  const isAssessmentPage = location.pathname.startsWith('/page/') || location.pathname === '/complete';
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH.ME);
        if (response.status === 200) {
          setIsAuthenticated(true);
          setUserName(response.data.name);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setUserName('');
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [location.pathname]); // Re-check when route changes
  
  const handleAuthAction = () => {
    if (isAuthenticated) {
      // Logout
      apiClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT)
        .then(() => {
          setIsAuthenticated(false);
          setUserName('');
          navigate('/login');
        })
        .catch(() => {
          // Even if logout fails, redirect to login
          setIsAuthenticated(false);
          setUserName('');
          navigate('/login');
        });
    } else {
      // Login
      navigate('/login');
    }
  };
  
  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-logo">
          <h1 className="logo-text">
            <span className="meka-text">MEKA</span>
            <span className="dealflow-text">Deal Flow</span>
          </h1>
        </div>
        <div className="header-right">
          <div className="header-title">
            <h1>3X Assessment</h1>
          </div>
          <div className="header-auth">
            {!isCheckingAuth && (
              <button 
                className="auth-button"
                onClick={handleAuthAction}
              >
                {isAuthenticated ? 'Logout' : 'Login'}
              </button>
            )}
          </div>
        </div>
      </div>
      {isAssessmentPage && (
        <div className="progress-indicator">
          {questionnaireData.map((_, index) => (
            <div
              key={index}
              className={`progress-dot ${
                completedPages.has(index + 1) ? 'completed' : ''
              } ${isPageAccessible(index + 1) ? 'accessible' : 'locked'}`}
            >
              {index + 1}
            </div>
          ))}
        </div>
      )}
    </header>
  );
};

function App() {
  const [responses, setResponses] = useState({});
  const [completedPages, setCompletedPages] = useState(new Set());

  const updateResponses = (pageId, pageResponses) => {
    setResponses(prev => ({
      ...prev,
      [pageId]: pageResponses
    }));
  };

  const markPageComplete = (pageId) => {
    setCompletedPages(prev => new Set([...prev, pageId]));
  };

  const isPageAccessible = (pageNumber) => {
    // Page 1 is always accessible
    if (pageNumber === 1) return true;
    
    // Other pages are accessible only if previous page is completed
    return completedPages.has(pageNumber - 1);
  };

  const allPagesCompleted = completedPages.size === questionnaireData.length;

  return (
    <Router>
      <div className="App">
        <AppHeader completedPages={completedPages} isPageAccessible={isPageAccessible} />

        <Routes>
          <Route path="/" element={<Navigate to="/page/1" replace />} />
          
          {/* Auth Routes */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/me" element={<UserProfilePage />} />
          <Route path="/create-code" element={<CreateCodePage />} />
          
          {/* Questionnaire Routes */}
          {questionnaireData.map((pageData, index) => (
            <Route
              key={index}
              path={`/page/${index + 1}`}
              element={
                <VerificationWrapper>
                  {isPageAccessible(index + 1) ? (
                    <QuestionnairePage
                      pageData={pageData}
                      pageNumber={index + 1}
                      responses={responses[index + 1] || {}}
                      onUpdateResponses={(pageResponses) => updateResponses(index + 1, pageResponses)}
                      onPageComplete={() => markPageComplete(index + 1)}
                      isLastPage={index === questionnaireData.length - 1}
                      allPagesCompleted={allPagesCompleted}
                    />
                  ) : (
                    <Navigate to="/page/1" replace />
                  )}
                </VerificationWrapper>
              }
            />
          ))}
          
          <Route
            path="/complete"
            element={
              <VerificationWrapper>
                {allPagesCompleted ? (
                  <CompletionPage responses={responses} />
                ) : (
                  <Navigate to="/page/1" replace />
                )}
              </VerificationWrapper>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
